import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  ArrowLeft, Plug, CheckCircle, AlertTriangle, XCircle, 
  Copy, RefreshCw, Code, FileText, Clock, Key, Eye, EyeOff, Send
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// URL de production pour l'API CRM
const PRODUCTION_API_URL = "https://mesubventions.fr";

export default function AdminCRMIntegration() {
  const [versions, setVersions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [apiKeys, setApiKeys] = useState([]);
  const [showKey, setShowKey] = useState({});
  const [newKeyName, setNewKeyName] = useState("");
  const [generatingKey, setGeneratingKey] = useState(false);

  useEffect(() => {
    fetchVersions();
    fetchApiKeys();
  }, []);

  const fetchVersions = async () => {
    try {
      const response = await axios.get(`${API}/crm/versions`);
      setVersions(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des versions");
    } finally {
      setLoading(false);
    }
  };

  const fetchApiKeys = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/crm/api-keys`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApiKeys(response.data.keys || []);
    } catch (error) {
      console.log("Pas de clés API existantes");
      setApiKeys([]);
    }
  };

  const generateApiKey = async () => {
    if (!newKeyName.trim()) {
      toast.error("Veuillez donner un nom à la clé");
      return;
    }
    setGeneratingKey(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/admin/crm/api-keys`, 
        { name: newKeyName },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setApiKeys([...apiKeys, response.data]);
      setNewKeyName("");
      toast.success("Clé API générée avec succès");
    } catch (error) {
      toast.error("Erreur lors de la génération de la clé");
    } finally {
      setGeneratingKey(false);
    }
  };

  const revokeApiKey = async (keyId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir révoquer cette clé ?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/admin/crm/api-keys/${keyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApiKeys(apiKeys.filter(k => k.id !== keyId));
      toast.success("Clé révoquée");
    } catch (error) {
      toast.error("Erreur lors de la révocation");
    }
  };

  const testAPI = async () => {
    setTestLoading(true);
    try {
      const response = await axios.post(`${API}/crm/v1/calculate`, {
        beneficiary_type: "particulier",
        income_rfr: 25000,
        household_size: 3,
        department: "75",
        housing_type: "maison",
        housing_age: 25,
        housing_surface: 120,
        work_types: ["pompe_chaleur"]
      });
      setTestResult({
        success: true,
        data: response.data
      });
      toast.success("API fonctionnelle !");
    } catch (error) {
      setTestResult({
        success: false,
        error: error.response?.data || error.message
      });
      toast.error("Erreur lors du test");
    } finally {
      setTestLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copié dans le presse-papier");
  };

  const toggleShowKey = (keyId) => {
    setShowKey(prev => ({ ...prev, [keyId]: !prev[keyId] }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin" className="text-slate-600 hover:text-slate-900">
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                  <Plug className="h-6 w-6 text-emerald-600" />
                  Intégration CRM
                </h1>
                <p className="text-slate-600 text-sm">Gérez les clés API et les versions pour votre CRM</p>
              </div>
            </div>
            <Button onClick={fetchVersions} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Actualiser
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Colonne principale */}
          <div className="lg:col-span-2 space-y-6">

            {/* Clés API */}
            <Card className="border-2 border-emerald-200">
              <CardHeader className="bg-emerald-50">
                <CardTitle className="flex items-center gap-2">
                  <Key className="h-5 w-5 text-emerald-600" />
                  Clés API pour votre CRM
                </CardTitle>
                <CardDescription>
                  Générez une clé API à configurer dans votre CRM (SuperBoard)
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                {/* Générer nouvelle clé */}
                <div className="flex gap-3 mb-6">
                  <div className="flex-1">
                    <Label htmlFor="keyName" className="sr-only">Nom de la clé</Label>
                    <Input
                      id="keyName"
                      placeholder="Nom de la clé (ex: CRM Production)"
                      value={newKeyName}
                      onChange={(e) => setNewKeyName(e.target.value)}
                    />
                  </div>
                  <Button onClick={generateApiKey} disabled={generatingKey}>
                    {generatingKey ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Key className="h-4 w-4 mr-2" />
                        Générer
                      </>
                    )}
                  </Button>
                </div>

                {/* Liste des clés */}
                {apiKeys.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Key className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>Aucune clé API générée</p>
                    <p className="text-sm">Créez une clé pour l'utiliser dans votre CRM</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {apiKeys.map((key) => (
                      <div key={key.id} className="bg-slate-50 rounded-lg p-4 border">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-slate-700">{key.name}</span>
                          <div className="flex items-center gap-2">
                            <Badge variant={key.active ? "default" : "secondary"}>
                              {key.active ? "Active" : "Révoquée"}
                            </Badge>
                            <span className="text-xs text-slate-500">
                              {new Date(key.created_at).toLocaleDateString('fr-FR')}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 bg-white px-3 py-2 rounded text-sm font-mono border">
                            {showKey[key.id] ? key.key : '•'.repeat(40)}
                          </code>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => toggleShowKey(key.id)}
                          >
                            {showKey[key.id] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => copyToClipboard(key.key)}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          {key.active && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => revokeApiKey(key.id)}
                            >
                              Révoquer
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Instructions */}
                <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h4 className="font-medium text-amber-800 mb-2">📋 Configuration dans votre CRM</h4>
                  <ol className="text-sm text-amber-700 space-y-1 list-decimal list-inside">
                    <li>Copiez la clé API ci-dessus</li>
                    <li>Dans SuperBoard → Paramètres → Clés API</li>
                    <li>Collez la clé dans le champ "API MesSubventions"</li>
                    <li>Enregistrez et testez la connexion</li>
                  </ol>
                </div>
              </CardContent>
            </Card>
            
            {/* Statut actuel */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-emerald-600" />
                  Statut de l'API
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-emerald-50 rounded-lg p-4">
                    <p className="text-sm text-emerald-700 font-medium">Version actuelle</p>
                    <p className="text-2xl font-bold text-emerald-900">{versions?.current_version}</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4">
                    <p className="text-sm text-blue-700 font-medium">Version recommandée</p>
                    <p className="text-2xl font-bold text-blue-900">{versions?.recommended_version}</p>
                  </div>
                </div>
                
                {versions?.migration_required && (
                  <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-amber-800">
                      <AlertTriangle className="h-5 w-5" />
                      <span className="font-medium">Migration recommandée</span>
                    </div>
                    <p className="text-sm text-amber-700 mt-1">
                      Une nouvelle version est disponible. Mettez à jour votre CRM pour bénéficier des dernières fonctionnalités.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Versions disponibles */}
            <Card>
              <CardHeader>
                <CardTitle>Versions disponibles</CardTitle>
                <CardDescription>Historique et changelog des versions de l'API</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {versions?.versions && Object.entries(versions.versions).map(([key, version]) => (
                    <div key={key} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <span className="text-xl font-bold">{version.version}</span>
                          <Badge variant={version.status === "stable" ? "default" : "secondary"}>
                            {version.status}
                          </Badge>
                          {key === versions.recommended_version && (
                            <Badge className="bg-emerald-100 text-emerald-800">Recommandée</Badge>
                          )}
                          {version.deprecation_date && (
                            <Badge variant="destructive">Dépréciée</Badge>
                          )}
                        </div>
                        <span className="text-sm text-slate-500 flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {version.release_date}
                        </span>
                      </div>
                      
                      <div className="bg-slate-50 rounded p-3 mb-3">
                        <p className="text-sm font-mono text-slate-700">{PRODUCTION_API_URL}{version.endpoint}</p>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="mt-1"
                          onClick={() => copyToClipboard(`${PRODUCTION_API_URL}${version.endpoint}`)}
                        >
                          <Copy className="h-3 w-3 mr-1" />
                          Copier l'URL
                        </Button>
                      </div>
                      
                      <div>
                        <p className="text-sm font-medium text-slate-700 mb-2">Changelog :</p>
                        <ul className="text-sm text-slate-600 space-y-1">
                          {version.changelog?.map((item, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <span className="text-emerald-500 mt-1">•</span>
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      {version.breaking_changes?.length > 0 && (
                        <div className="mt-3 bg-red-50 rounded p-3">
                          <p className="text-sm font-medium text-red-700 mb-2">⚠️ Breaking Changes :</p>
                          <ul className="text-sm text-red-600 space-y-1">
                            {version.breaking_changes.map((item, i) => (
                              <li key={i}>• {item}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Test API */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Code className="h-5 w-5" />
                  Tester l'API
                </CardTitle>
                <CardDescription>Vérifiez que l'API fonctionne correctement</CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={testAPI} disabled={testLoading}>
                  {testLoading ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Test en cours...
                    </>
                  ) : (
                    <>
                      <Plug className="h-4 w-4 mr-2" />
                      Lancer un test
                    </>
                  )}
                </Button>
                
                {testResult && (
                  <div className={`mt-4 rounded-lg p-4 ${testResult.success ? 'bg-emerald-50' : 'bg-red-50'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {testResult.success ? (
                        <>
                          <CheckCircle className="h-5 w-5 text-emerald-600" />
                          <span className="font-medium text-emerald-800">Test réussi</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="h-5 w-5 text-red-600" />
                          <span className="font-medium text-red-800">Test échoué</span>
                        </>
                      )}
                    </div>
                    <pre className="text-xs bg-white rounded p-3 overflow-auto max-h-60">
                      {JSON.stringify(testResult.success ? testResult.data : testResult.error, null, 2)}
                    </pre>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            
            {/* URL de Production */}
            <Card className="border-emerald-200 bg-emerald-50">
              <CardHeader>
                <CardTitle className="text-lg text-emerald-800">🌐 URL de Production</CardTitle>
              </CardHeader>
              <CardContent>
                <code className="block bg-white px-3 py-2 rounded text-sm font-mono border border-emerald-200 mb-3">
                  {PRODUCTION_API_URL}
                </code>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full"
                  onClick={() => copyToClipboard(PRODUCTION_API_URL)}
                >
                  <Copy className="h-3 w-3 mr-2" />
                  Copier l'URL
                </Button>
              </CardContent>
            </Card>

            {/* Endpoints disponibles */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Endpoints disponibles</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-sm">
                  <p className="font-medium text-slate-700">Calcul (stable)</p>
                  <code className="text-xs bg-slate-100 px-2 py-1 rounded block mt-1">
                    POST /api/crm/v1/calculate
                  </code>
                </div>
                <div className="text-sm">
                  <p className="font-medium text-slate-700">Versions</p>
                  <code className="text-xs bg-slate-100 px-2 py-1 rounded block mt-1">
                    GET /api/crm/versions
                  </code>
                </div>
                <div className="text-sm">
                  <p className="font-medium text-slate-700">Vérification</p>
                  <code className="text-xs bg-slate-100 px-2 py-1 rounded block mt-1">
                    GET /api/crm/check?current_version=v1
                  </code>
                </div>
              </CardContent>
            </Card>

            {/* Code pour votre CRM */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Code pour votre CRM</CardTitle>
                <CardDescription>Vérification automatique des mises à jour</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 mb-3">
                  Ajoutez ce code dans votre CRM pour vérifier les mises à jour :
                </p>
                <div className="bg-slate-900 rounded-lg p-3 text-xs text-slate-100 overflow-auto">
                  <pre>{`// Vérifier 1x/jour
const checkUpdate = async () => {
  const res = await fetch(
    '${PRODUCTION_API_URL}/api/crm/check?current_version=v1'
  );
  const data = await res.json();
  
  if (data.action_required) {
    // Afficher notification admin
    notify(data.message);
  }
};`}</pre>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="mt-2"
                  onClick={() => copyToClipboard(`const checkUpdate = async () => {\n  const res = await fetch('${PRODUCTION_API_URL}/api/crm/check?current_version=v1');\n  const data = await res.json();\n  if (data.action_required) {\n    notify(data.message);\n  }\n};`)}
                >
                  <Copy className="h-3 w-3 mr-1" />
                  Copier le code
                </Button>
              </CardContent>
            </Card>

            {/* Notification CRM */}
            <Card className="border-blue-200 bg-blue-50">
              <CardHeader>
                <CardTitle className="text-lg text-blue-800 flex items-center gap-2">
                  <Send className="h-5 w-5" />
                  Notifier le CRM
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-blue-700 mb-3">
                  Quand une nouvelle version sera disponible, vous pourrez envoyer une notification directement à votre CRM.
                </p>
                <Button variant="outline" className="w-full" disabled>
                  <Send className="h-4 w-4 mr-2" />
                  Envoyer notification (bientôt)
                </Button>
              </CardContent>
            </Card>

          </div>
        </div>
      </div>
    </div>
  );
}
